import { ComponentType, SVGProps } from 'react';
import { ChevronRightIcon } from '@zep-us/zus';
import { ParseKeys } from 'i18next';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { cn } from '@zep/utils';
import { Button, type ButtonProps } from '../Button';
import { ProductButton, ProductTheme } from './types';
import { useProductButtonStyles } from './useProductButtonStyles';
interface ProductSectionHeaderProps {
  logo: ComponentType<SVGProps<SVGSVGElement>>;
  headerText: ParseKeys<['home']>;
  primaryButton: ProductButton;
  secondaryButton: ProductButton;
  theme: ProductTheme;
}
interface LinkedButtonProps {
  button: ProductButton;
  variant?: ButtonProps['variant'];
  intent?: ButtonProps['intent'];
  size?: ButtonProps['size'];
  style?: ButtonProps['style'];
  className?: ButtonProps['className'];
  children?: React.ReactNode;
}
const LinkedButton = ({
  button,
  children,
  ...buttonProps
}: LinkedButtonProps) => {
  const {
    t
  } = useTranslation(['home']);
  return <Button asChild {...buttonProps} data-sentry-element="Button" data-sentry-component="LinkedButton" data-sentry-source-file="ProductSectionHeader.tsx">
      <Link href={button.href} onClick={button.onClick} data-sentry-element="Link" data-sentry-source-file="ProductSectionHeader.tsx">
        {t(button.label)}
        {children}
      </Link>
    </Button>;
};
export const ProductSectionHeader = ({
  logo: LogoComponent,
  headerText,
  primaryButton,
  secondaryButton,
  theme
}: ProductSectionHeaderProps) => {
  const {
    t
  } = useTranslation(['home']);
  const {
    primaryStyle,
    primaryClassName,
    secondaryStyle,
    secondaryClassName
  } = useProductButtonStyles(theme);
  return <div className={cn('flex flex-col tablet:flex-row', 'gap-quiz-lg tablet:gap-quiz-xl', 'items-start tablet:items-end', 'justify-between', 'w-full', 'sticky top-0')} data-sentry-component="ProductSectionHeader" data-sentry-source-file="ProductSectionHeader.tsx">
      {/* Header Content */}
      <div className="flex flex-1 flex-col items-start gap-quiz-md lg:gap-quiz-lg">
        <LogoComponent className="h-[36px] w-auto tablet:h-[50px]" data-sentry-element="LogoComponent" data-sentry-source-file="ProductSectionHeader.tsx" />
        <h2 className="text-quiz-heading-lg-extrabold text-quiz-gray-900 tablet:text-quiz-heading-xl-extrabold lg:text-quiz-display-sm-extrabold">
          {t(headerText)}
        </h2>
      </div>

      {/* Header Buttons */}
      <div className="flex shrink-0 items-start gap-quiz-2xs">
        <LinkedButton button={primaryButton} variant="contained" intent="strong" size="lg" style={primaryStyle} className={primaryClassName} data-sentry-element="LinkedButton" data-sentry-source-file="ProductSectionHeader.tsx">
          <ChevronRightIcon data-sentry-element="ChevronRightIcon" data-sentry-source-file="ProductSectionHeader.tsx" />
        </LinkedButton>
        <LinkedButton button={secondaryButton} variant="outlined" intent="alternative" size="lg" style={secondaryStyle} className={secondaryClassName} data-sentry-element="LinkedButton" data-sentry-source-file="ProductSectionHeader.tsx">
          <ChevronRightIcon data-sentry-element="ChevronRightIcon" data-sentry-source-file="ProductSectionHeader.tsx" />
        </LinkedButton>
      </div>
    </div>;
};